import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import {
  ClinicalNoteType,
  IClinicalNote,
  IClinicalNoteActivityEvent,
  IClinicalNoteInput,
  ClinicalNoteData,
} from '../../entities/ClinicalNote/ClinicalNote';

export class ClinicalNoteApi extends BaseApi {
  /**
   * Per-note-type URL builder. The backend exposes a single ClinicalNotesController
   * with the note type as a path parameter:
   *   /api/Patients/{id}/ClinicalNotes/{noteType}/...
   */
  private static path(noteType: ClinicalNoteType, suffix: string = ''): string {
    return `${BaseApi.apiUrl}/Patients/:id/ClinicalNotes/${noteType}${suffix}`;
  }

  private static deleteBase: string = `${BaseApi.apiUrl}/ClinicalNotes`;

  public static async getClinicalNotes(
    patientId: string,
    noteType: ClinicalNoteType,
  ): Promise<ClinicalNoteData> {
    return this.sendApiRequest<ClinicalNoteData>({
      method: HttpMethodEnum.GET,
      path: ClinicalNoteApi.path(noteType),
      data: undefined,
      id: patientId,
    });
  }

  public static async putClinicalNote(
    patientId: string,
    noteType: ClinicalNoteType,
    data: IClinicalNoteInput,
  ): Promise<IClinicalNote> {
    return this.sendApiRequest<IClinicalNote>({
      method: HttpMethodEnum.PUT,
      path: ClinicalNoteApi.path(noteType),
      data: data as any,
      id: patientId,
    });
  }

  public static async deleteClinicalNote(id: string): Promise<void> {
    return this.sendApiRequest<void>({
      method: HttpMethodEnum.DELETE,
      path: `${ClinicalNoteApi.deleteBase}/:id`,
      data: undefined,
      id: id,
    });
  }

  public static async getActivityHistory(
    patientId: string,
    noteType: ClinicalNoteType,
  ): Promise<IClinicalNoteActivityEvent[]> {
    return this.sendApiRequest<IClinicalNoteActivityEvent[]>({
      method: HttpMethodEnum.GET,
      path: ClinicalNoteApi.path(noteType, '/ActivityHistory'),
      data: undefined,
      id: patientId,
    });
  }

  public static async downloadClinicalNotesPdf(
    patientId: string,
    noteType: ClinicalNoteType,
    language: string,
    includeActivityLog: boolean,
  ): Promise<Blob> {
    return this.sendApiRequest<Blob>({
      method: HttpMethodEnum.GET,
      path: `${ClinicalNoteApi.path(noteType, '/pdf')}?language=${language}&includeActivityLog=${includeActivityLog}`,
      data: undefined,
      id: patientId,
      extraOptionsConfig: { responseType: 'blob' },
    });
  }

  public static async sendClinicalNotesToAssistant(
    patientId: string,
    noteType: ClinicalNoteType,
    language: string,
  ): Promise<{ assistantName: string; assistantEmail: string }> {
    return this.sendApiRequest<{
      assistantName: string;
      assistantEmail: string;
    }>({
      method: HttpMethodEnum.POST,
      path: `${ClinicalNoteApi.path(noteType, '/SendToAssistant')}?language=${language}`,
      data: undefined,
      id: patientId,
    });
  }

  // ---------------------------------------------------------------
  // AMI-389 — Counselling dual acknowledgement
  // ---------------------------------------------------------------

  public static async signCounsellingNote(noteId: string): Promise<IClinicalNote> {
    return this.sendApiRequest<IClinicalNote>({
      method: HttpMethodEnum.POST,
      path: `${BaseApi.apiUrl}/ClinicalNotes/:id/Sign`,
      data: undefined,
      id: noteId,
    });
  }

  public static async getCounsellingNoteForPatient(noteId: string): Promise<IClinicalNote> {
    return this.sendApiRequest<IClinicalNote>({
      method: HttpMethodEnum.GET,
      path: `${BaseApi.apiUrl}/Patient/ClinicalNotes/:id`,
      data: undefined,
      id: noteId,
    });
  }

  public static async getCounsellingNoteForPatientByTask(taskId: string): Promise<IClinicalNote> {
    return this.sendApiRequest<IClinicalNote>({
      method: HttpMethodEnum.GET,
      path: `${BaseApi.apiUrl}/Patient/Tasks/:id/CounsellingNote`,
      data: undefined,
      id: taskId,
    });
  }

  public static async acknowledgeCounsellingNote(
    noteId: string,
    notes: string | null,
  ): Promise<IClinicalNote> {
    return this.sendApiRequest<IClinicalNote>({
      method: HttpMethodEnum.POST,
      path: `${BaseApi.apiUrl}/Patient/ClinicalNotes/:id/Acknowledge`,
      data: { notes } as any,
      id: noteId,
    });
  }

  public static downloadPdfFile(pdfBlob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename || `ClinicalNotes_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
